"use client";
import {
    Box, Button, DialogContent, Modal, ModalClose, ModalDialog,
    ModalOverflow, Stack, Typography, Chip, Sheet, Checkbox,
    FormControl, FormLabel, Slider, Select, Option, Divider,
    Input, Grid, LinearProgress
} from "@mui/joy";
import { useState, useEffect, useContext } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { LayoutContext } from "../../context";
import { APICreateTest, APIGetContentTree, APICreateTgPromoInvoice, APICreateStandardInvoice, detectTrialLocked } from "../../api";
import { useNavigate } from "react-router";
import { Lock } from "@phosphor-icons/react";
import LimitReachedModal from "./LimitReachedModal";



export default function GenerateTestModal() {
    const { tests, setTests, loadUserTests, generateTestModalOpen, setGenerateTestModalOpen, token, user, openUpgradeModal } = useContext(LayoutContext);
    const navigate = useNavigate();
    const [limitReached, setLimitReached] = useState(false);
    const [limitMessage, setLimitMessage] = useState('');
    const [limitVariant, setLimitVariant] = useState('paywall');

    const [categories, setCategories] = useState([]);
    const [selectedCategories, setSelectedCategories] = useState([]);
    const [loading, setLoading] = useState(false);
    const [questionStats, setQuestionStats] = useState(null);
    const [formData, setFormData] = useState({
        title: '',
        mode: 'study',
        question_selection_mode: 'new',
        total_questions: 20,
        category_ids: []
    });

    // Загрузка категорий при открытии модального окна
    useEffect(() => {
        if (generateTestModalOpen) {
            loadCategories();
        }
    }, [generateTestModalOpen]);

    const loadCategories = async () => {
        try {
            const data = await APIGetContentTree(token);
            if (data.success) {
                setCategories(data.categories);
                if (data.stats) {
                    setQuestionStats(data.stats);
                }
            }
        } catch (error) {
            console.error('Error fetching categories:', error);
        }
    };

    // Вычисление доступных вопросов в зависимости от выбранного типа
    const getAvailableQuestionsCount = () => {
        if (!questionStats) return null;

        switch (formData.question_selection_mode) {
            case 'new':
                return questionStats.remaining || 0;
            case 'incorrect':
                return questionStats.incorrect_count || 0;
            case 'favourite':
                return questionStats.favourite_count || 0;
            case 'mixed':
            default:
                return questionStats.total_questions || 0;
        }
    };

    const handleCloseModal = () => {
        setGenerateTestModalOpen(false);
        setSelectedCategories([]);
        setFormData({
            title: '',
            mode: 'study',
            question_selection_mode: 'new',
            total_questions: 20,
            category_ids: []
        });
    };

    // Функция для получения всех ID дочерних категорий (рекурсивно)
    const getAllChildCategoryIds = (category) => {
        let childIds = [];
        
        if (category.children && category.children.length > 0) {
            category.children.forEach(child => {
                childIds.push(child.id);
                childIds = [...childIds, ...getAllChildCategoryIds(child)];
            });
        }
        
        return childIds;
    };

    // Функция для получения всех ID категорий в поддереве (включая саму категорию)
    const getAllCategoryIdsInSubtree = (category) => {
        return [category.id, ...getAllChildCategoryIds(category)];
    };

    // Функция проверки, выбраны ли все дочерние категории
    const areAllChildrenSelected = (category) => {
        const allChildIds = getAllChildCategoryIds(category);
        return allChildIds.length > 0 && allChildIds.every(id => selectedCategories.includes(id));
    };

    // Функция проверки, выбрана ли хотя бы одна дочерняя категория
    const isAnyChildSelected = (category) => {
        const allChildIds = getAllChildCategoryIds(category);
        return allChildIds.some(id => selectedCategories.includes(id));
    };

    // Обработчик переключения категории (с автоматическим выбором дочерних)
    const handleCategoryToggle = (category) => {
        const allCategoryIdsInSubtree = getAllCategoryIdsInSubtree(category);
        const isCurrentlySelected = selectedCategories.includes(category.id);

        setSelectedCategories(prev => {
            let newSelection;
            
            if (isCurrentlySelected) {
                // Если категория выбрана - снимаем выбор со всей ветки
                newSelection = prev.filter(id => !allCategoryIdsInSubtree.includes(id));
            } else {
                // Если категория не выбрана - выбираем всю ветку
                newSelection = [...prev, ...allCategoryIdsInSubtree];
                // Убираем дубликаты
                newSelection = [...new Set(newSelection)];
            }
            
            setFormData(prevFormData => ({
                ...prevFormData,
                category_ids: newSelection
            }));
            
            return newSelection;
        });
    };

    // Получить ВСЕ ID категорий из дерева (рекурсивно)
    const getAllCategoryIds = (cats) => {
        let ids = [];
        for (const cat of cats) {
            ids.push(cat.id);
            if (cat.children && cat.children.length > 0) {
                ids = [...ids, ...getAllCategoryIds(cat.children)];
            }
        }
        return ids;
    };

    // Проверка: все ли категории выбраны
    const allCategoryIds = categories.length > 0 ? getAllCategoryIds(categories) : [];
    const isAllSelected = allCategoryIds.length > 0 && allCategoryIds.every(id => selectedCategories.includes(id));

    // Обработчик чекбокса "ВСЕ ВОПРОСЫ"
    const handleSelectAll = () => {
        if (isAllSelected) {
            setSelectedCategories([]);
            setFormData(prev => ({ ...prev, category_ids: [] }));
        } else {
            setSelectedCategories(allCategoryIds);
            setFormData(prev => ({ ...prev, category_ids: allCategoryIds }));
        }
    };

    // Обработчик переключения только одной категории (без дочерних)
    const handleSingleCategoryToggle = (categoryId) => {
        setSelectedCategories(prev => {
            const newSelection = prev.includes(categoryId)
                ? prev.filter(id => id !== categoryId)
                : [...prev, categoryId];
            
            setFormData(prevFormData => ({
                ...prevFormData,
                category_ids: newSelection
            }));
            
            return newSelection;
        });
    };

    const handleGenerateTest = async () => {
        if (selectedCategories.length === 0) {
            alert('Выберите хотя бы одну категорию');
            return;
        }

        setLoading(true);
        try {
            const data = await APICreateTest(formData, token);

            if (data.success) {
                handleCloseModal();
                navigate(`/test-session/${data.test.id}`);
                loadUserTests();
            } else {
                alert('Ошибка при генерации теста: ' + (data.error || 'Неизвестная ошибка'));
            }
        } catch (error) {
            // Trial lock (категория вне триального пула или триал закончился) —
            // вместо "лимит исчерпан" открываем UpgradeModal с внятным reason.
            const trialLock = detectTrialLocked(error);
            if (trialLock) {
                setGenerateTestModalOpen(false);
                openUpgradeModal(trialLock.message);
                return;
            }
            // Paywall vs quota:
            //  - paywall:true → плашка с тарифами
            //  - daily_limit_reached:true БЕЗ paywall → плашка без тарифов («приходи завтра»)
            const data = error.response?.data;
            const isPaywall = data?.paywall === true;
            const isQuota = data?.daily_limit_reached === true && !isPaywall;
            if (isPaywall || isQuota) {
                setLimitMessage(data.error || '');
                setLimitVariant(isPaywall ? 'paywall' : 'quota');
                setLimitReached(true);
                setGenerateTestModalOpen(false);
                return;
            }
            console.error('Error creating test:', error);
            alert('Ошибка при создании теста: ' + (data?.error || error.message));
        } finally {
            setLoading(false);
        }
    };
    
    // Компонент для отображения категории с индикатором состояния
    const CategoryItem = ({ category, level = 0 }) => {
        const hasChildren = category.children && category.children.length > 0;
        const isSelected = selectedCategories.includes(category.id);
        const allChildrenSelected = hasChildren && areAllChildrenSelected(category);
        const someChildrenSelected = hasChildren && isAnyChildSelected(category) && !allChildrenSelected;

        return (
            <Box sx={{ ml: level * 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                    <Checkbox
                        checked={isSelected}
                        indeterminate={someChildrenSelected}
                        onChange={() => handleCategoryToggle(category)}
                        label={category.name}
                        color="primary"
                        sx={{
                            fontWeight: hasChildren ? 'bold' : 'normal',
                            '& .MuiCheckbox-checkbox': {
                                borderColor: 'primary.400',
                            },
                            '& .MuiCheckbox-label': {
                                color: 'text.primary',
                            }
                        }}
                    />
                    <Chip size="sm" variant="soft" color="neutral" sx={{ ml: 1 }}>
                        {category.question_count} вопросов
                    </Chip>
                    {hasChildren && (
                        <Chip size="sm" variant="outlined" color="primary" sx={{ ml: 1 }}>
                            {category.children.length} подкат.
                        </Chip>
                    )}
                </Box>
                {hasChildren && (
                    <Box sx={{ ml: 2 }}>
                        {category.children.map(child => (
                            <CategoryItem
                                key={child.id} 
                                category={child} 
                                level={level + 1}
                            />
                        ))}
                    </Box>
                )}
            </Box>
        );
    };

    const renderCategoryTree = (categories) => {
        return categories.map(category => (
            <CategoryItem key={category.id} category={category} />
        ));
    };

    return (<>
        <AnimatePresence>
            {generateTestModalOpen && (
                <Modal
                    open={generateTestModalOpen}
                    onClose={handleCloseModal}
                    slotProps={{
                        backdrop: {
                            component: motion.div,
                            initial: { opacity: 0, backdropFilter: 'blur(0px)' },
                            animate: { opacity: 1, backdropFilter: 'blur(8px)' },
                            exit: { opacity: 0, backdropFilter: 'blur(0px)' },
                            transition: { duration: 0.3 },
                        },
                    }}
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        zIndex: 10012
                    }}
                >
                    <ModalOverflow sx={{ height: 'auto', maxHeight: '90vh' }}>
                        <ModalDialog
                            component={motion.div}
                            initial={{ opacity: 0, scale: 0.8, transform: 'translate(0, -5%)' }}
                            animate={{ opacity: 1, scale: 1, transform: 'translate(0, 0%)' }}
                            exit={{ opacity: 0, scale: 0.8, transform: 'translate(0, -5%)' }}
                            transition={{ duration: 0.3 }}
                            sx={{
                                width: { xs: '95%', sm: '90%', md: 800 },
                                maxWidth: 800,
                                p: 0,
                                borderRadius: 'xl',
                                overflow: 'hidden',
                            }}
                        >
                            <ModalClose />
                            
                            <DialogContent sx={{ px: 3, pb: 3, mt: 5 }}>
                                <Stack spacing={3}>
                                    <Typography level="h3">Создание теста</Typography>
                                    
                                    {/* Настройки теста */}
                                    <Stack spacing={2}>
                                        <FormControl>
                                            <FormLabel>Название теста</FormLabel>
                                            <Input
                                                placeholder="Введите название теста"
                                                value={formData.title}
                                                onChange={(e) => setFormData(prev => ({
                                                    ...prev,
                                                    title: e.target.value
                                                }))}
                                            />
                                        </FormControl>

                                        <Stack direction="row" spacing={2}>
                                            <FormControl sx={{ flex: 1 }}>
                                                <FormLabel>Режим тестирования</FormLabel>
                                                <Select
                                                    slotProps={{ listbox: { sx: { zIndex: 10013 } } }}
                                                    value={formData.mode}
                                                    onChange={(e, value) => setFormData(prev => ({
                                                        ...prev,
                                                        mode: value
                                                    }))}
                                                >
                                                    <Option value="study">Учебный</Option>
                                                    <Option value="exam">Тестовый</Option>
                                                </Select>
                                            </FormControl>

                                            <FormControl sx={{ flex: 1 }}>
                                                <FormLabel>
                                                    Тип вопросов
                                                    {getAvailableQuestionsCount() !== null && (
                                                        <Chip
                                                            size="sm"
                                                            variant="soft"
                                                            color="primary"
                                                            sx={{ ml: 1 }}
                                                        >
                                                            {getAvailableQuestionsCount()} доступно
                                                        </Chip>
                                                    )}
                                                </FormLabel>
                                                <Select
                                                    slotProps={{ listbox: { sx: { zIndex: 10013 } } }}
                                                    value={formData.question_selection_mode}
                                                    onChange={(e, value) => setFormData(prev => ({
                                                        ...prev,
                                                        question_selection_mode: value
                                                    }))}
                                                >
                                                    <Option value="new">Только новые</Option>
                                                    <Option value="incorrect">Только с ошибками</Option>
                                                    <Option value="favourite">Только избранные</Option>
                                                    <Option value="mixed">Все вопросы</Option>
                                                </Select>
                                            </FormControl>
                                        </Stack>

                                        <Box>
                                            <Typography level="body-sm" fontWeight="md" sx={{ mb: 1 }}>
                                                Количество вопросов: {formData.total_questions}
                                            </Typography>
                                            <Slider
                                                value={formData.total_questions}
                                                min={10}
                                                max={60}
                                                step={1}
                                                onChange={(e, value) => setFormData(prev => ({
                                                    ...prev,
                                                    total_questions: value
                                                }))}
                                                valueLabelDisplay="auto"
                                            />

                                            {/* Поле для ручного ввода */}
                                            <Input
                                                type="number"
                                                value={formData.total_questions}
                                                onChange={(e) => {
                                                    const value = parseInt(e.target.value) || 1;
                                                    setFormData(prev => ({
                                                        ...prev,
                                                        total_questions: value
                                                    }));
                                                }}
                                                slotProps={{
                                                    input: {
                                                        min: 1,
                                                        max: 60
                                                    }
                                                }}
                                                sx={{ mt: 1 }}
                                            />
                                            <Typography level="body-xs" sx={{ mt: 0.5 }}>
                                                Минимум: 1, максимум: 60 вопросов
                                            </Typography>
                                        </Box>
                                    </Stack>

                                    <Divider />

                                    {/* Выбор категорий */}
                                    <Box>
                                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                                            <Typography level="body-sm" fontWeight="md">
                                                Выберите категории
                                            </Typography>
                                            <Checkbox
                                                checked={isAllSelected}
                                                indeterminate={selectedCategories.length > 0 && !isAllSelected}
                                                onChange={handleSelectAll}
                                                label="ВСЕ ВОПРОСЫ"
                                                size="sm"
                                                sx={{
                                                    fontWeight: 600,
                                                    '& .MuiCheckbox-label': {
                                                        fontSize: '0.8rem',
                                                    }
                                                }}
                                            />
                                        </Box>
                                        <Box sx={{
                                            maxHeight: 300,
                                            overflowY: 'auto',
                                            border: '1px solid',
                                            borderColor: 'divider',
                                            borderRadius: 'md',
                                            p: 2,
                                            bgcolor: 'background.surface'
                                        }}>
                                            {categories.length > 0 ? (
                                                renderCategoryTree(categories)
                                            ) : (
                                                <Typography color="neutral">
                                                    Загрузка категорий...
                                                </Typography>
                                            )}
                                        </Box>
                                    </Box>

                                    {/* Кнопки */}
                                    <Stack direction="row" spacing={1} justifyContent="flex-end">
                                        <Button 
                                            variant="outlined" 
                                            color="neutral"
                                            onClick={handleCloseModal}
                                        >
                                            Отмена
                                        </Button>
                                        <Button
                                            type="button"
                                            variant="solid"
                                            color="primary"
                                            onClick={handleGenerateTest}
                                            loading={loading}
                                            disabled={selectedCategories.length === 0}
                                        >
                                            Сгенерировать тест
                                        </Button>
                                    </Stack>
                                </Stack>
                            </DialogContent>
                        </ModalDialog>
                    </ModalOverflow>
                </Modal>
            )}
        </AnimatePresence>

        {/* Модалка лимита (paywall с тарифами / quota без тарифов) */}
        <LimitReachedModal
            open={limitReached}
            onClose={() => setLimitReached(false)}
            message={limitMessage}
            token={token}
            variant={limitVariant}
        />
    </>
    );
}